/**
 * The cradle pair's load-time consistency check (D-TBD; H-1's residual risk, `docs/research/raw/2026-10-19-helm-dock-
 * diagnosis.md` §1). The pair lives in two places: the Career (`parkedL`, `carriedBy`, a hangar entry's
 * `cradle.carried`) and the worker (the member out of the pool at `CARRIED_UNTIL`, `FleetShipView.carried`). Every
 * live path writes both halves, but an old, hand-edited or half-written save (H-1: the dev `parkOwnL` wrote only the
 * Career half) can load with one half only, and every pair action then refuses `notAllowed` with nothing on screen.
 *
 * The worker is the authority on where a ship is, so the repair follows it:
 *
 * - **Flown ship first.** When the worker's flown ship (`FleetShipView.flown`, none = the own ship) is not the
 *   Career's (`flownShipId`), the save was cut mid switch (e.g. a helm switch: the worker flies the L while the Career
 *   still has it parked). Nothing is repaired then (a drop would strand the L the worker flies, and a release can't
 *   name the right carrier); the mismatch is only reported.
 * - **Career-only** (the Career names a pair member the worker does not hold at `CARRIED_UNTIL`, or does not hold at
 *   all): the Career's half is dropped. `parkedL` goes, a `carriedBy` goes with that L's cradle emptied, and a cradle
 *   that names the S is emptied. A dropped L takes the station the worker shows it at (when it shows one), so the
 *   hangar never holds a ship that is nowhere. The ship stays wherever the worker has it. A member the worker flies
 *   is never dropped.
 * - **Worker-only** (the worker holds a ship at `CARRIED_UNTIL` the Career doesn't name): it is returned to the pool
 *   docked at the pilot's station (`launchFromCarrier` with a `stationId`, the flown ship as the carrier; a reload
 *   always docks the pilot), which `release` lists for the caller to send. Only a ship the Career still owns (a
 *   hangar entry): the Career drops a destroyed L first (`loseParkedL`), so a save in between must not bring it back;
 *   such a ship is listed in `kept`. The Career's flown ship is never released.
 *
 * Pure: the input Career is never changed. A consistent pair returns the input itself and nothing to release.
 */
import type { Career, OwnedShip } from '../career/types'
import { OWN_SHIP_ID, ownedShip } from './hangar'

/** The worker view fields the check reads (`FleetShipView`). */
export interface PairCheckShip {
  readonly id: string
  readonly stationId: string | null
  /** `CARRIED_UNTIL` in the worker; absent = false. */
  readonly carried?: boolean
  /** The worker's flown ship (`BOARDED_UNTIL`); absent = false. */
  readonly flown?: boolean
}

export interface PairCheck {
  /** The Career with its Career-only half dropped; the input itself when there is none. */
  readonly career: Career
  /** The Career-only pair members dropped (fleet ids). */
  readonly dropped: readonly string[]
  /** The worker-only `CARRIED_UNTIL` ships to return to the pool (fleet ids). */
  readonly release: readonly string[]
  /** Worker-only `CARRIED_UNTIL` ships the Career no longer owns: left as they are (fleet ids). */
  readonly kept: readonly string[]
  /** The flown ships disagree (null id = the own ship): nothing repaired; null when they agree. */
  readonly flownMismatch: { readonly career: string | null; readonly worker: string | null } | null
}

/** The Career's flown ship as a worker id (null = the own ship, which has no worker slot). */
export function careerFlownId(career: Career): string | null {
  const f = career.flownShip ?? OWN_SHIP_ID
  return f === OWN_SHIP_ID ? null : f
}

/** The fleet ids the Career expects out of the pool at `CARRIED_UNTIL` (the own ship has no worker slot). */
export function expectedCarried(career: Career): Set<string> {
  const out = new Set<string>()
  const flown = career.flownShip ?? OWN_SHIP_ID
  if (career.parkedL !== undefined) out.add(career.parkedL.fleetId)
  if (career.carriedBy !== undefined) out.add(career.carriedBy)
  // A cradle's S: the flown L's, or a stowed one. The flown S riding back in its parked L is the worker's flown ship.
  for (const e of career.hangar ?? []) {
    const s = e.cradle?.carried ?? null
    if (s !== null && s !== flown) out.add(s)
  }
  // The own (lease) ship in a cradle has no worker slot: never expected, so its cradle is never emptied.
  out.delete(OWN_SHIP_ID)
  return out
}

const NONE: readonly string[] = Object.freeze([])

/** Checks the Career's half of the pair against the worker's `ships` (see the file header). */
export function checkCarrierPair(career: Career, ships: readonly PairCheckShip[]): PairCheck {
  const carried = new Set<string>()
  const stationOf = new Map<string, string | null>()
  let workerFlown: string | null = null
  for (const v of ships) {
    stationOf.set(v.id, v.stationId)
    if (v.carried === true) carried.add(v.id)
    if (v.flown === true) workerFlown = v.id
  }
  const flown = careerFlownId(career)
  if (workerFlown !== flown) return { career, dropped: NONE, release: NONE, kept: NONE, flownMismatch: { career: flown, worker: workerFlown } }

  const expected = expectedCarried(career)
  const dropped = [...expected].filter((id) => !carried.has(id) && id !== workerFlown)
  const strays = [...carried].filter((id) => !expected.has(id) && id !== flown)
  const release = strays.filter((id) => ownedShip(career, id) !== null)
  const kept = strays.filter((id) => ownedShip(career, id) === null)
  if (dropped.length === 0) return { career, dropped, release, kept, flownMismatch: null }

  const drop = new Set(dropped)
  const next: Career = { ...career }
  /** L ids whose Career half goes: their cradle empties and they take the worker's station. */
  const lGone = new Set<string>()
  if (career.parkedL !== undefined && drop.has(career.parkedL.fleetId)) {
    lGone.add(career.parkedL.fleetId)
    delete next.parkedL
  }
  if (career.carriedBy !== undefined && drop.has(career.carriedBy)) {
    lGone.add(career.carriedBy)
    delete next.carriedBy
  }
  if (career.hangar !== undefined) {
    next.hangar = career.hangar.map((e): OwnedShip => {
      const s = e.cradle?.carried ?? null
      const emptyCradle = lGone.has(e.fleetId) || (s !== null && drop.has(s))
      const at = lGone.has(e.fleetId) ? (stationOf.get(e.fleetId) ?? null) : null
      if (!emptyCradle && at === null) return e
      const out: OwnedShip = { ...e }
      if (emptyCradle && e.cradle !== undefined) out.cradle = { carried: null }
      if (at !== null) out.stationId = at
      return out
    })
  }
  return { career: next, dropped, release, kept, flownMismatch: null }
}

/** A release as the runner sent it (`error` null = the worker accepted it). */
export interface ReleaseOutcome {
  readonly id: string
  readonly error: string | null
}

/**
 * The one warning a check logs, written after the releases were sent (`released`, their outcomes); null when the
 * pair agrees.
 */
export function pairCheckWarning(check: Pick<PairCheck, 'dropped' | 'kept' | 'flownMismatch'>, released: readonly ReleaseOutcome[]): string | null {
  const m = check.flownMismatch
  if (m !== null) {
    const name = (id: string | null): string => id ?? 'the own ship'
    return `parkedL: the loaded cradle pair was not checked: the worker flies ${name(m.worker)}, the Career ${name(m.career)} (nothing repaired)`
  }
  if (check.dropped.length === 0 && released.length === 0 && check.kept.length === 0) return null
  const parts: string[] = []
  if (check.dropped.length > 0) parts.push(`Career-only ${check.dropped.join(', ')} dropped`)
  const back = released.filter((r) => r.error === null).map((r) => r.id)
  if (back.length > 0) parts.push(`worker-only ${back.join(', ')} returned to the pool`)
  for (const r of released) if (r.error !== null) parts.push(`worker-only ${r.id} not returned (${r.error})`)
  if (check.kept.length > 0) parts.push(`worker-only ${check.kept.join(', ')} left out of the pool (no hangar entry)`)
  return `parkedL: the loaded cradle pair disagrees with the worker (${parts.join('; ')})`
}

/** True when the check has nothing to repair or report. */
export function pairAgrees(check: PairCheck): boolean {
  return check.flownMismatch === null && check.dropped.length === 0 && check.release.length === 0 && check.kept.length === 0
}
